/**
 * Public widget resource — the UNAUTHENTICATED surface used by the embeddable
 * widget app (ARCHITECTURE §7). Scoped by `cid` (`ORG_ID-WIDGET_ID`); the widget
 * loads config, creates a session, and posts visitor messages here. Auth is an
 * opaque session token (not a principal JWT), injected by the widget's HttpClient.
 */

import type { HttpClient } from '../httpClient';
import { endpoints } from '../endpoints';
import type {
  ChatSession,
  Message,
  MessageMedia,
  PreChatFormValues,
  VoiceToken,
  WidgetConfig,
} from '../../types';

/** Response of create-session: the session plus its resume token. */
export interface CreateWidgetSessionResult {
  session: ChatSession;
  /** Opaque signed token; persisted to localStorage for resume (ARCHITECTURE §7). */
  sessionToken: string;
}

export interface CreateWidgetSessionBody {
  preChatForm?: PreChatFormValues;
  /** Optional commerce context passed via `&ctx=` on the embed. */
  context?: Record<string, unknown>;
  /** Page/locale context captured on the host page; server adds IP + UA-derived fields. */
  visitorContext?: Record<string, unknown>;
}

export interface WidgetSendMessageBody {
  body?: string;
  clientMsgId: string;
  /** Attachments (each from a prior `uploadMedia` call). */
  media?: MessageMedia[];
}

export class WidgetPublicResource {
  constructor(private readonly http: HttpClient) {}

  /** Public theme + pre-chat form for the given embed id (anonymous). */
  config(cid: string): Promise<WidgetConfig> {
    return this.http.get(endpoints.widgetPublic.config(cid), { anonymous: true });
  }

  /** Create a visitor session (anonymous → returns a resume token). */
  createSession(
    cid: string,
    body: CreateWidgetSessionBody,
  ): Promise<CreateWidgetSessionResult> {
    return this.http.post(endpoints.widgetPublic.createSession(cid), body, {
      anonymous: true,
    });
  }

  /** Fetch a session on resume (uses the session token, not anonymous). */
  /** The session's transcript (oldest-first) — used to rehydrate the widget after a reload. */
  history(cid: string, sessionId: string, limit = 50): Promise<Message[]> {
    return this.http.get(endpoints.widgetPublic.messages(cid, sessionId), { query: { limit } });
  }

  /** Visitor ends the conversation (status → left); the widget then shows the CSAT screen. */
  leave(cid: string, sessionId: string): Promise<{ ok: boolean }> {
    return this.http.post(endpoints.widgetPublic.close(cid, sessionId));
  }

  getSession(cid: string, sessionId: string): Promise<ChatSession> {
    return this.http.get(endpoints.widgetPublic.session(cid, sessionId));
  }

  send(cid: string, sessionId: string, body: WidgetSendMessageBody): Promise<Message> {
    return this.http.post(endpoints.widgetPublic.messages(cid, sessionId), body);
  }

  /** Upload a visitor attachment (session-token scoped); returns a descriptor for `send({ media })`. */
  uploadMedia(cid: string, sessionId: string, file: File): Promise<MessageMedia> {
    const fd = new FormData();
    fd.append('file', file);
    return this.http.post(endpoints.widgetPublic.upload(cid, sessionId), undefined, {
      formData: fd,
    });
  }

  /** Submit the post-chat CSAT: overall `rating` (1-5), optional separate `agentRating`
   *  (dual-rating mode), and an optional shared comment. Uses the session token. */
  submitRating(
    cid: string,
    sessionId: string,
    rating: number,
    comment?: string,
    agentRating?: number,
  ): Promise<{ ok: boolean }> {
    return this.http.post(endpoints.widgetPublic.rating(cid, sessionId), {
      rating,
      agentRating,
      comment,
    });
  }

  /** Mint a voice access token so the visitor can call the agent handling the chat (session-token
   *  scoped). Rejects when voice isn't configured or no agent has claimed the chat. */
  voiceToken(cid: string, sessionId: string): Promise<VoiceToken> {
    return this.http.post(endpoints.widgetPublic.voiceToken(cid, sessionId), {});
  }

  /** Anonymous visitor-presence beat (live-visitors panel); every ~15s. The ACK may carry an
   *  agent-initiated session handoff (engagedSessionId + sessionToken) the widget must adopt. */
  visitorHeartbeat(
    cid: string,
    body: { visitorId: string; pageUrl?: string; language?: string; sessionId?: string },
  ): Promise<{ ok: boolean; engagedSessionId?: string | null; sessionToken?: string | null }> {
    return this.http.post(endpoints.widgetPublic.visitorHeartbeat(cid), body, {
      anonymous: true,
    });
  }

  /** Ship one co-browse capture (session-token scoped; server 409s unless an agent started). */
  cobrowseFrame(
    cid: string,
    sessionId: string,
    frame: Record<string, unknown>,
  ): Promise<{ ok: boolean }> {
    return this.http.post(endpoints.widgetPublic.cobrowseFrame(cid, sessionId), frame);
  }

  /** Visitor withdraws screen-sharing consent — ends the co-browse for both sides. */
  cobrowseStop(cid: string, sessionId: string): Promise<{ ok: boolean }> {
    return this.http.post(endpoints.widgetPublic.cobrowseStop(cid, sessionId));
  }

  /** Offline leave-a-message (anonymous): becomes a ticket + owner/visitor emails. */
  offlineMessage(
    cid: string,
    body: { name: string; email: string; phone?: string; message: string },
  ): Promise<{ ok: boolean }> {
    return this.http.post(endpoints.widgetPublic.offlineMessage(cid), body, { anonymous: true });
  }

  /** Queue slot while the chat waits unclaimed (session-token scoped). */
  queue(
    cid: string,
    sessionId: string,
  ): Promise<{ waiting: boolean; position: number | null; estimatedWaitSeconds: number | null }> {
    return this.http.get(endpoints.widgetPublic.queue(cid, sessionId));
  }

  /** Record a GDPR consent acceptance (anonymous, before a session exists). */
  submitConsent(cid: string, contentVersion?: string): Promise<{ ok: boolean }> {
    return this.http.post(
      endpoints.widgetPublic.consent(cid),
      { contentVersion },
      { anonymous: true },
    );
  }
}
